import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { AlertError } from './componentes-utiles/AlertError';
import SHA256 from 'crypto-js/sha256';

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const textoMayusculas = (texto) => String(texto ?? '').toLocaleUpperCase('es-ES');

export const compararTexto = (a, b) => String(a ?? '').localeCompare(String(b ?? ''), 'es', { sensitivity: 'base' });

export const ordenarPorCampoTexto = (array = [], campo = 'nombre') => (
    [...(Array.isArray(array) ? array : [])].sort((a, b) => compararTexto(a?.[campo], b?.[campo]))
);

export const ordenarOpcionesPorLabel = (array = []) => (
    [...(Array.isArray(array) ? array : [])].sort((a, b) => compararTexto(a?.label, b?.label))
);

export const esProyectoVisible = (proyecto = {}) => (
    Number(proyecto?.value ?? proyecto?.id) === 0 || Number(proyecto?.oculto ?? 0) === 0
);

export const filtrarProyectosVisibles = (proyectos = []) => (
    (Array.isArray(proyectos) ? proyectos : []).filter(esProyectoVisible)
);


// "2026-01-08 08:00:00" -> { fecha: "08/01/2026", hora: "08:00" }
const parseDateTime = (dt) => {
    if (!dt || typeof dt !== "string") return { fecha: "", hora: "" };
    const [datePart, timePart] = dt.split(" ");
    if (!datePart) return { fecha: "", hora: "" };
    const [y, m, d] = datePart.split("-");
    const hora = timePart ? timePart.slice(0, 5) : "";
    return { fecha: `${d}/${m}/${y}`, hora };
};

const formatFechaES = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

const nombreCompleto = (f) => {
    const n = f?.usuario_nombre ?? "";
    const a = f?.usuario_apellidos ?? "";
    return `${n} ${a}`.trim();
};

const getUsuarioId = (f) => f?.usuario_id ?? f?.id_usuario ?? "sin_usuario";

const groupByUsuario = (arr) => {
    const map = new Map();
    for (const f of arr) {
        const key = getUsuarioId(f);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(f);
    }
    return map;
};

// Orden: por apellidos+nombre y luego por hora_inicio
const sortFichajes = (a, b) => {
    const na = `${a?.usuario_apellidos ?? ""} ${a?.usuario_nombre ?? ""}`.trim().toLowerCase();
    const nb = `${b?.usuario_apellidos ?? ""} ${b?.usuario_nombre ?? ""}`.trim().toLowerCase();
    if (na < nb) return -1;
    if (na > nb) return 1;

    // ISO "YYYY-MM-DD HH:mm:ss" se ordena bien como string
    const da = a?.hora_inicio ?? "";
    const db = b?.hora_inicio ?? "";
    if (da < db) return -1;
    if (da > db) return 1;
    return 0;
};

export const exportarPDF = (fichajes = [], nombreArchivo = "fichajes", meta = {}) => {
    try {
        const lista = Array.isArray(fichajes) ? [...fichajes] : [];
        lista.sort(sortFichajes);

        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

        const marginLeft = 12;
        const marginRight = 12;
        const headerHeight = 34; // espacio reservado arriba para título + datos empleado
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const availableWidth = pageWidth - marginLeft - marginRight;

        const head = [[
            "Fecha",
            "Inicio",
            "Fin",
            "Total",
            "Observación",
            "Corrección",
        ]];

        // Proporciones de columnas (suma 1)
        const colRatio = {
            fecha: 0.10,
            inicio: 0.08,
            fin: 0.08,
            total: 0.10,
            observacion: 0.54,
            correccion: 0.10,
        };

        const grupos = groupByUsuario(lista);
        const entries = Array.from(grupos.entries());

        if (entries.length === 0) {
            doc.setFontSize(12);
            doc.text("No hay fichajes para los filtros seleccionados.", 14, 20);
            doc.save(`${nombreArchivo}.pdf`);
            return true;
        }

        // Función para dibujar cabecera del usuario (se reutiliza por página)
        const drawHeader = (usuarioId, usuarioNombre, totalRegistros, totalSegsUsuario) => {
            doc.setFontSize(16);
            doc.text("Reporte de fichajes", marginLeft, 14);

            doc.setFontSize(11);
            doc.text(`Empleado: ${usuarioNombre} (ID: ${usuarioId})`, marginLeft, 21);

            doc.setFontSize(10);
            doc.text(`Rango: ${formatFechaES(meta.fechaDesde)} - ${formatFechaES(meta.fechaHasta)}`, marginLeft, 27);

            doc.text(`Registros: ${totalRegistros}`, 210, 21);
            doc.text(`Total horas: ${formatTime(totalSegsUsuario)}`, 210, 27);
        };

        entries.forEach(([usuarioId, userRows], index) => {
            // Orden dentro del usuario (por hora_inicio)
            userRows.sort((a, b) => (a?.hora_inicio ?? "").localeCompare(b?.hora_inicio ?? ""));

            const usuarioNombre = nombreCompleto(userRows[0]);
            const totalSegsUsuario = userRows.reduce((acc, f) => acc + (Number(f?.total) || 0), 0);

            const body = userRows.map((f) => {
                const ini = parseDateTime(f?.hora_inicio);
                const fin = parseDateTime(f?.hora_fin);
                return [
                    ini.fecha || fin.fecha || "",
                    ini.hora,
                    fin.hora,
                    formatTime(Number(f?.total) || 0),
                    f?.observacion ?? "",
                    Number(f?.correccion_aceptada) === 1 ? "Sí" : "No",
                ];
            });

            // Dibujamos cabecera inicial (para primera página)
            drawHeader(usuarioId, usuarioNombre, userRows.length, totalSegsUsuario);

            autoTable(doc, {
                margin: { left: marginLeft, right: marginRight, top: headerHeight },
                tableWidth: availableWidth,
                head,
                body,
                styles: {
                    fontSize: 8.5,
                    cellPadding: 2,
                    valign: "middle",
                    overflow: "linebreak",
                },
                headStyles: {
                    fillColor: [30, 30, 30],
                    textColor: 255,
                    fontStyle: "bold",
                },
                columnStyles: {
                    0: { cellWidth: availableWidth * colRatio.fecha },
                    1: { cellWidth: availableWidth * colRatio.inicio },
                    2: { cellWidth: availableWidth * colRatio.fin },
                    3: { cellWidth: availableWidth * colRatio.total },
                    4: { cellWidth: availableWidth * colRatio.observacion },
                    5: { cellWidth: availableWidth * colRatio.correccion },
                },

                // ✅ Antes de dibujar la tabla en cada página
                willDrawPage: () => {
                    drawHeader(usuarioId, usuarioNombre, userRows.length, totalSegsUsuario);
                },

                // ✅ Después: solo pie, no header
                didDrawPage: () => {
                    const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(9);
                    doc.text(
                        `Página ${pageNumber} / ${pageCount}`,
                        pageWidth - marginRight - 25,
                        pageHeight - 8
                    );
                },
            });

            // Al terminar este usuario, si hay más usuarios, saltamos de página
            if (index < entries.length - 1) {
                doc.addPage();
            }
        });

        doc.save(`${nombreArchivo}.pdf`);
        return true;
    } catch (e) {
        console.error("Error exportando PDF:", e);
        return false;
    }
};



export const isColorDark = (color) => {
    // El color debe estar en formato hexadecimal (#rrggbb)
    let r, g, b;
    if (color.length === 6) {
        r = parseInt(color.slice(1, 3), 16);
        g = parseInt(color.slice(3, 5), 16);
        b = parseInt(color.slice(5, 7), 16);
    } else {
        return false; // Devuelve falso si el color no es hexadecimal válido
    }

    // Calcula el brillo
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128; // Un brillo menor de 128 se considera oscuro
};


export const obtenerFechaHoraSQL = () => {
    const fechaActual = new Date();

    // Obtener los componentes de la fecha y hora
    const anio = fechaActual.getFullYear();
    const mes = String(fechaActual.getMonth() + 1).padStart(2, '0'); // Sumar 1 al mes porque es cero-indexado
    const dia = String(fechaActual.getDate()).padStart(2, '0');
    const horas = String(fechaActual.getHours()).padStart(2, '0');
    const minutos = String(fechaActual.getMinutes()).padStart(2, '0');
    const segundos = String(fechaActual.getSeconds()).padStart(2, '0');

    // Formatear la cadena de fecha y hora para SQL DATETIME
    return `${anio}-${mes}-${dia} ${horas}:${minutos}:${segundos}`;
};

//generar color
export const generarColorAleatorio = () => {
    return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
}

// devolver el nombre del mes
export const mesName = (mes) => {
    const meses = [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];

    // Validar que el número del mes sea válido
    if (mes < 1 || mes > 12) {
        return "Mes inválido";
    }

    // Retornar el nombre del mes correspondiente
    return meses[mes - 1];
};

export const formatDateTime = (inputDateTime) => {
    const dateTime = new Date(inputDateTime.replace(" ", "T")); // Convertir a formato Date

    const dd = String(dateTime.getDate()).padStart(2, '0');  // Día
    const mm = String(dateTime.getMonth() + 1).padStart(2, '0'); // Mes
    const yy = String(dateTime.getFullYear()).slice(-2); // Últimos 2 dígitos del año

    const hh = String(dateTime.getHours()).padStart(2, '0');  // Horas
    const min = String(dateTime.getMinutes()).padStart(2, '0'); // Minutos

    return `${dd}/${mm}/${yy} ${hh}:${min}`;
}

export const formatMinutosHoras = (minutos) => {
    if (!minutos || isNaN(minutos)) return "0:00"; // Manejo de valores inválidos

    let horas = Math.floor(minutos / 60);
    let minutosRestantes = Math.round(minutos % 60); // Obtener minutos restantes

    return `${horas}:${minutosRestantes.toString().padStart(2, "0")}`; // Formato hh:mm
};

export const parseHorasMinutos = (valor) => {
    const [horas, minutos] = valor.split(":").map(Number);
    return horas * 60 + (minutos || 0); // Convertir a minutos totales
};
export function generarDiasDelMes(mes, anio) {
    const dias = [];
    const fecha = new Date(anio, mes, 1);

    while (fecha.getMonth() === mes) {
        dias.push({
            fecha: new Date(fecha),
            dia: fecha.getDate(),
            nombreDia: fecha.toLocaleDateString('es-ES', { weekday: 'long' })
        });
        fecha.setDate(fecha.getDate() + 1);
    }

    return dias;
}
export function formatTime(segundos) {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const seg = segundos % 60;
    return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:${String(seg).padStart(2, '0')}`;
}

export function exportarExcel(data, nombreArchivo) {
    if (!data || data.length === 0) return false;

    const workbook = XLSX.utils.book_new();

    const formatTime = (seconds) => {
        if (!seconds) return "00:00:00";
        const h = Math.floor(seconds / 3600).toString().padStart(2, "0");
        const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
        const s = Math.floor(seconds % 60).toString().padStart(2, "0");
        return `${h}:${m}:${s}`;
    };

    /* ======================================================
       FECHAS → FORMATO ESPAÑOL
    ====================================================== */
    const formatearFechaHora = (fechaStr) => {
        if (!fechaStr) return "";

        const fecha = new Date(fechaStr.replace(" ", "T")); // asegura compatibilidad
        if (isNaN(fecha)) return fechaStr;

        return fecha.toLocaleString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
        });
    };

    const formatearFecha = (fechaStr) => {
        if (!fechaStr) return "";

        const fecha = new Date(fechaStr.replace(" ", "T"));
        if (isNaN(fecha)) return fechaStr;

        return fecha.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    };


    /* ======================================================
       HOJA 1 → FICHAJES DETALLADOS
    ====================================================== */
    const detallesFichajes = data.map(item => {
        const usuarioNombreCompleto = `${item.usuario_nombre} ${item.usuario_apellidos}`;

        return {
            "ID Fichaje": item.id,
            "Usuario": usuarioNombreCompleto,
            "ID Usuario": item.id_usuario,

            "Hora Inicio": formatearFechaHora(item.hora_inicio),
            "Hora Fin": formatearFechaHora(item.hora_fin),
            "Duración (hh:mm:ss)": formatTime(item.total),

            "Latitud Inicio": item.latitud_inicio || "",
            "Longitud Inicio": item.longitud_inicio || "",
            "Latitud Fin": item.latitud_fin || "",
            "Longitud Fin": item.longitud_fin || "",

            "Punto Inicio": item.id_punto_fichaje_inicio || "",
            "Punto Fin": item.id_punto_fichaje_fin || "",

            "Tarjeta Inicio": item.id_tarjeta_inicio || "",
            "Tarjeta Fin": item.id_tarjeta_fin || "",

            "Corregido Por (ID)": item.id_usuario_corrector || "",
            "ID Fichaje Corrector": item.id_fichaje_corrector || "",
            "Fecha Corrección": formatearFechaHora(item.fecha_corrector),
            "Corrección Aceptada":
                item.correccion_aceptada === 1 ? "Sí" :
                    item.correccion_aceptada === 2 ? "No" :
                        item.correccion_aceptada === 0 ? "No" :
                            "",

            "Eliminado": item.eliminado == "1" ? "Sí" : "No",
            "Fecha Anulación": formatearFechaHora(item.fecha_anulacion),
            "Usuario Anulador (ID)": item.id_usuario_anulador || "",

            "Observación": item.observacion || "",

            "Hash": item.hash || "",
            "Hash Anterior": item.hash_anterior || ""
        };
    });

    const sheetFichajes = XLSX.utils.json_to_sheet(detallesFichajes);
    XLSX.utils.book_append_sheet(workbook, sheetFichajes, "Fichajes");


    /* ============================================================
    HOJA 2 → RESUMEN COMPLETO
 ============================================================ */
    const resumenUsuarios = {};
    const resumenDias = {};
    let fechaDesde = null;
    let fechaHasta = null;

    data.forEach(item => {
        const usuario = `${item.usuario_nombre} ${item.usuario_apellidos}`;
        const fecha = item.hora_inicio.split(" ")[0];
        const dur = item.total || 0;

        /* --------------------------
           RESUMEN POR USUARIO
        --------------------------- */
        if (!resumenUsuarios[usuario]) {
            resumenUsuarios[usuario] = { duracion: 0, cantidad: 0 };
        }
        resumenUsuarios[usuario].duracion += dur;
        resumenUsuarios[usuario].cantidad++;

        /* --------------------------
           RESUMEN POR DÍA
        --------------------------- */
        if (!resumenDias[fecha]) resumenDias[fecha] = 0;
        resumenDias[fecha] += dur;

        /* --------------------------
           RANGO FECHAS
        --------------------------- */
        if (!fechaDesde || fecha < fechaDesde) fechaDesde = fecha;
        if (!fechaHasta || fecha > fechaHasta) fechaHasta = fecha;
    });

    /* ------------------------------------------------------------
       ARRAY PARA EXPORTAR AL EXCEL
    ------------------------------------------------------------ */

    // ① Resumen general
    const resumenGeneral = [
        {
            "Resumen General": "Total Horas", Valor: formatTime(
                Object.values(resumenUsuarios).reduce((sum, u) => sum + u.duracion, 0)
            )
        },
        { "Resumen General": "Fecha Desde", Valor: formatearFecha(fechaDesde) },
        { "Resumen General": "Fecha Hasta", Valor: formatearFecha(fechaHasta) },
        {}
    ];

    // ② Resumen por día
    const resumenDiasArray = [
        { "Resumen por Día": "" },
        ...Object.entries(resumenDias).map(([fecha, dur]) => ({
            Fecha: formatearFecha(fecha),
            "Horas Totales": formatTime(dur)
        })),
        {}
    ];

    // ③ Resumen por usuario
    const resumenUsuariosArray = [
        { "Resumen por Usuario": "" },
        ...Object.entries(resumenUsuarios).map(([usuario, stats]) => ({
            Usuario: usuario,
            "Total Horas": formatTime(stats.duracion),
            "Cantidad Fichajes": stats.cantidad
        }))
    ];

    // Unión en una única hoja
    const hojaResumenData = [
        ...resumenGeneral,
        ...resumenDiasArray,
        ...resumenUsuariosArray
    ];

    const sheetResumen = XLSX.utils.json_to_sheet(hojaResumenData);
    XLSX.utils.book_append_sheet(workbook, sheetResumen, "Resumen");

    /* ======================================================
       HOJA 3 → FICHAS POR DÍA
    ====================================================== */
    const porDia = {};

    data.forEach(item => {
        const fecha = item.hora_inicio.split(" ")[0];
        if (!porDia[fecha]) porDia[fecha] = 0;
        if (item.total) porDia[fecha] += item.total;
    });

    const sheetGrafico = XLSX.utils.json_to_sheet(
        Object.entries(porDia).map(([fecha, seg]) => ({
            Fecha: formatearFecha(fecha),
            "Horas Totales": (seg / 3600).toFixed(2)
        }))
    );

    XLSX.utils.book_append_sheet(workbook, sheetGrafico, "Gráfico");


    /* ======================================================
       HOJAS POR USUARIO
    ====================================================== */
    const fichajesPorUsuario = {};

    data.forEach(item => {
        const usuario = `${item.usuario_nombre} ${item.usuario_apellidos}`;

        if (!fichajesPorUsuario[usuario]) {
            fichajesPorUsuario[usuario] = [];
        }

        const registro = detallesFichajes.find(f => f["ID Fichaje"] === item.id);
        fichajesPorUsuario[usuario].push(registro);
    });

    Object.entries(fichajesPorUsuario).forEach(([usuario, registros]) => {
        const nombreHoja = usuario.length > 31 ? usuario.substring(0, 31) : usuario;
        const ws = XLSX.utils.json_to_sheet(registros);
        XLSX.utils.book_append_sheet(workbook, ws, nombreHoja);
    });


    /* ======================================================
       EXPORTACIÓN
    ====================================================== */
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    saveAs(blob, `${nombreArchivo}.xlsx`);
    return true;
}



export const obtenerFechaCompleta = (diaTexto) => {
    // Ej: "L12/4" → extraer día y mes
    const match = diaTexto.match(/[A-Z](\d{1,2})\/(\d{1,2})/);
    if (!match) return diaTexto;

    const dia = parseInt(match[1], 10);
    const mes = parseInt(match[2], 10) - 1; // Date usa 0-indexed meses
    const hoy = new Date();
    const fecha = new Date(hoy.getFullYear(), mes, dia);

    const diasLargos = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const mesesLargos = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

    return `${diasLargos[fecha.getDay()]} ${fecha.getDate()} de ${mesesLargos[fecha.getMonth()]}`;
};


export const verificarHashFichaje = (fichaje) => {
    const datos_para_hash = JSON.stringify({
        hash_anterior: fichaje.hash_anterior,
        hora_inicio: fichaje.hora_inicio,
        hora_fin: fichaje.hora_fin,
        id_usuario: fichaje.usuario_id,
        observacion: fichaje.observacion ?? "",
        lat: fichaje.lat,
        lng: fichaje.lng,
    });

    const hash_calculado = SHA256(datos_para_hash).toString();

    return {
        valido: hash_calculado === fichaje.hash,
        hash_calculado,
    };
};


export const convertirEnlaces = (texto) => {
    // Expresión regular para detectar URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    return texto.split(urlRegex).map((part, index) => {
        // Si la parte coincide con una URL, crear un enlace
        if (part.match(urlRegex)) {
            return (
                <a
                    key={index}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2', textDecoration: 'underline' }}
                >
                    {part}
                </a>
            );
        }
        // Devolver texto normal si no es una URL
        return part;
    });
};

export function refactorizarTlf(numero) {
    if (!numero) return '';

    // Si el número no contiene espacios y no empieza con +34, devolverlo tal cual
    if (!numero.includes(' ') && !numero.startsWith('+34')) {
        return numero;
    }

    // Eliminar todos los espacios y el prefijo +34 si existe
    return numero.replace(/\s/g, '').replace(/^\+34/, '');
}

export const formatoFechaEs = (fechaStr) => {
    if (!fechaStr) return "";
    const fecha = new Date(fechaStr);
    return fecha.toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
    });
};
export const formatearFecha =(fechaStr) => {
    if (!fechaStr) return "";

    // fechaStr = "2025-12-05 13:20:34"
    const [fecha, hora] = fechaStr.split(" ");

    if (!fecha || !hora) return "";

    const [yyyy, MM, dd] = fecha.split("-");
    const [hh, mm] = hora.split(":");

    return `${hh}:${mm} ${dd}/${MM}/${yyyy}`;
}
